import * as React from 'react';

import { Ga4Events } from '@notino/web-tracking/dist/types/package/ga4/events';

import { useTrackingContext } from '@context/tracking/TrackingContext';

import {
  RICH_CONTENT_SECTION_PERCENTS,
  trackRichContentSectionView,
} from './tracking';

/** Minimum time between viewing the start and end of a section to count it as "seen". */
const DWELL_MS = 2000;

/**
 * Tracks how far down the rich content the user gets. The rich content is
 * overlaid with three equally sized section markers (0-33%, 34-66%, 67-100%).
 * A section fires a `component_view` event once the user has scrolled to its
 * end AND at least {@link DWELL_MS} have elapsed since the section's start
 * first entered the viewport.
 *
 * If the user scrolls out of the section (in either direction) before the
 * dwell threshold is met, the section state is reset so re-entering starts
 * the dwell over from scratch. This avoids false positives when the user
 * scrolls quickly past the section and then back.
 *
 * Tracking re-arms on `page_view` so it fires at most once per page view, even
 * during client-side navigation between products.
 */
export const useTrackRichContentSectionView = () => {
  const { getTimeFromInit } = useTrackingContext();
  const topMarkerRefs = React.useRef<Array<HTMLElement | null>>(
    RICH_CONTENT_SECTION_PERCENTS.map(() => null)
  );
  const bottomMarkerRefs = React.useRef<Array<HTMLElement | null>>(
    RICH_CONTENT_SECTION_PERCENTS.map(() => null)
  );
  const sectionMarkerRefs = React.useRef<Array<HTMLElement | null>>(
    RICH_CONTENT_SECTION_PERCENTS.map(() => null)
  );

  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const firedSections = new Set<number>();
    const startTimes: Array<number | undefined> = [];
    const dwellTimers: Array<ReturnType<typeof setTimeout> | undefined> = [];
    let hasScrolled = false;

    const markUserScrolled = () => {
      hasScrolled = true;
    };
    window.addEventListener('scroll', markUserScrolled, { passive: true });

    const fireSection = (index: number) => {
      firedSections.add(index);
      trackRichContentSectionView({
        section: RICH_CONTENT_SECTION_PERCENTS[index],
        interaction: hasScrolled ? 'scroll' : 'automatic',
        timing: getTimeFromInit(),
      });
      const topEl = topMarkerRefs.current[index];
      const bottomEl = bottomMarkerRefs.current[index];
      const sectionEl = sectionMarkerRefs.current[index];
      if (topEl) observer.unobserve(topEl);
      if (bottomEl) observer.unobserve(bottomEl);
      if (sectionEl) observer.unobserve(sectionEl);
    };

    const resetSection = (index: number) => {
      if (dwellTimers[index] !== undefined) {
        clearTimeout(dwellTimers[index]);
        dwellTimers[index] = undefined;
      }
      startTimes[index] = undefined;
    };

    const handleTopEntry = (
      index: number,
      entry: IntersectionObserverEntry
    ) => {
      if (entry.isIntersecting && startTimes[index] === undefined) {
        startTimes[index] = performance.now();
      }
    };

    const handleBottomEntry = (
      index: number,
      entry: IntersectionObserverEntry
    ) => {
      if (entry.isIntersecting) {
        const startTime = startTimes[index] ?? performance.now();
        startTimes[index] = startTime;
        const elapsed = performance.now() - startTime;
        if (elapsed >= DWELL_MS) {
          fireSection(index);
        } else {
          dwellTimers[index] = setTimeout(
            () => fireSection(index),
            DWELL_MS - elapsed
          );
        }
      } else if (dwellTimers[index] !== undefined) {
        clearTimeout(dwellTimers[index]);
        dwellTimers[index] = undefined;
      }
    };

    const handleSectionEntry = (
      index: number,
      entry: IntersectionObserverEntry
    ) => {
      if (entry.isIntersecting) {
        return;
      }
      // Section fully out of view (above OR below). If we hadn't reached
      // the dwell threshold yet, reset so a return to the section starts
      // the dwell measurement fresh.
      if (startTimes[index] === undefined) {
        return;
      }
      const elapsed = performance.now() - startTimes[index]!;
      if (elapsed < DWELL_MS) {
        resetSection(index);
      }
    };

    const handleEntry = (entry: IntersectionObserverEntry) => {
      const target = entry.target as HTMLElement;

      const topIndex = topMarkerRefs.current.indexOf(target);
      if (topIndex !== -1) {
        if (firedSections.has(topIndex)) return;
        handleTopEntry(topIndex, entry);
        return;
      }

      const bottomIndex = bottomMarkerRefs.current.indexOf(target);
      if (bottomIndex !== -1) {
        if (firedSections.has(bottomIndex)) return;
        handleBottomEntry(bottomIndex, entry);
        return;
      }

      const sectionIndex = sectionMarkerRefs.current.indexOf(target);
      if (sectionIndex !== -1 && !firedSections.has(sectionIndex)) {
        handleSectionEntry(sectionIndex, entry);
      }
    };

    const observer = new IntersectionObserver((entries) =>
      entries.forEach(handleEntry)
    );

    const observeSections = () => {
      topMarkerRefs.current.forEach(
        (element) => element && observer.observe(element)
      );
      bottomMarkerRefs.current.forEach(
        (element) => element && observer.observe(element)
      );
      sectionMarkerRefs.current.forEach(
        (element) => element && observer.observe(element)
      );
    };
    observeSections();

    // A new page_view resets the session, so re-arm tracking for every section.
    const reArmOnPageView = ({ detail }: CustomEvent<Ga4Events>) => {
      if (detail?.event !== 'page_view') {
        return;
      }
      firedSections.clear();
      startTimes.length = 0;
      hasScrolled = false;
      dwellTimers.forEach(
        (timer) => timer !== undefined && clearTimeout(timer)
      );
      dwellTimers.length = 0;
      observeSections();
    };
    document.addEventListener(
      'ga4-tracking-event',
      reArmOnPageView as EventListener
    );

    return () => {
      observer.disconnect();
      dwellTimers.forEach(
        (timer) => timer !== undefined && clearTimeout(timer)
      );
      window.removeEventListener('scroll', markUserScrolled);
      document.removeEventListener(
        'ga4-tracking-event',
        reArmOnPageView as EventListener
      );
    };
  }, [getTimeFromInit]);

  const setTopMarkerRef = (index: number) => (element: HTMLElement | null) => {
    topMarkerRefs.current[index] = element;
  };

  const setBottomMarkerRef =
    (index: number) => (element: HTMLElement | null) => {
      bottomMarkerRefs.current[index] = element;
    };

  const setSectionMarkerRef =
    (index: number) => (element: HTMLElement | null) => {
      sectionMarkerRefs.current[index] = element;
    };

  return { setTopMarkerRef, setBottomMarkerRef, setSectionMarkerRef };
};
